// ─────────────────────────────────────────────────────────────────────────────
// AddNodeWizard — the three-step "Add New Node" flow from the design:
//   1 · details    — IP + node name          (./DetailsStep)
//   2 · preparing  — the create request      (./PreparingStep)
//   3 · ready      — enrollment token        (./ReadyStep)
//
// Wired to POST /enterprise-nodes. This file owns the request, the step machine
// and the dismissal rules; the steps are presentation.
//
// The backend generates the node_id, assigns the default ports and DETECTS geo
// from the IP, so the body is exactly { publicIp, name } — there is nothing else
// for this form to collect.
//
// WHY STEP 3 CANNOT BE DISMISSED BY ACCIDENT: the 201 carries a SINGLE-USE
// enrollment token, valid one hour, and this screen is where the admin copies it
// or downloads the config file containing it. So the overlay click and the Escape
// key are both inert on step 3 — only the explicit button closes it.
//
// This is now a convenience rather than a hard guarantee. Losing the token used to
// mean deleting the node and re-adding it; it no longer does. POST
// /enterprise-nodes/:id/config re-serves the same token while it is valid and
// issues a fresh one after it expires, for any node that is not currently online
// — which is what the row menu's Download Node Core action uses.
//
// Step 2 is not dismissable either, but for a different reason: the request is in
// flight and a node may already have been created, so abandoning it would hide a
// row that now exists.
//
// A create can SUCCEED WITH WARNINGS — an unreachable IP or a failed geo lookup
// still creates the node (201 + `warnings`). That is not an error state and must
// not be rendered as one; ReadyStep shows them as advisories.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./AddNodeWizard.module.css";
import Stepper from "./Stepper";
import DetailsStep from "./DetailsStep";
import PreparingStep from "./PreparingStep";
import ReadyStep from "./ReadyStep";
import { CloseIcon } from "./icons";
import { createNode } from "../../../../services/nodesService";

const STEP = { DETAILS: 1, PREPARING: 2, READY: 3 };
const TOTAL_STEPS = 3;

export default function AddNodeWizard({ onClose, onCreated }) {
  const [step, setStep]     = useState(STEP.DETAILS);
  const [form, setForm]     = useState({ ip: "", name: "" });
  const [error, setError]   = useState(null);
  const [created, setCreated] = useState(null);   // the 201 payload

  // Step 2: request in flight. Step 3: the token is on screen exactly once.
  const locked = step === STEP.PREPARING || step === STEP.READY;
  const dismiss = useCallback(() => { if (!locked) onClose(); }, [locked, onClose]);
  const patchForm = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismiss]);

  // The actual create. Advances to READY on success; drops back to DETAILS with
  // the reason on failure, so the admin can fix the field and retry — a 400 here
  // is a verdict on what they typed.
  const submit = useCallback(async () => {
    setStep(STEP.PREPARING);
    setError(null);
    try {
      const res = await createNode({ publicIp: form.ip, name: form.name });
      setCreated(res);
      setStep(STEP.READY);
      // Let the page refetch now — the node exists and belongs in the table even
      // though the admin is still reading its token.
      onCreated?.(res);
    } catch (err) {
      setError(err.message);
      setStep(STEP.DETAILS);
    }
  }, [form.ip, form.name, onCreated]);

  return createPortal(
    <div className={styles.overlay} onClick={dismiss}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-node-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.stepBar}>
          <Stepper current={step} total={TOTAL_STEPS} />
          <button
            type="button"
            className={styles.close}
            onClick={dismiss}
            disabled={locked}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </header>

        <div className={styles.body}>
          {step === STEP.DETAILS && (
            <DetailsStep
              form={form}
              error={error}
              onChange={patchForm}
              onContinue={submit}
            />
          )}
          {step === STEP.PREPARING && <PreparingStep nodeName={form.name} />}
          {step === STEP.READY && created && (
            <ReadyStep created={created} form={form} onClose={onClose} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
